import { defineMessages } from 'react-intl';

export default defineMessages({
  // Geographic dimensions
  country: 'Country',
  countryDescription: 'The country of the visitor, determined by their IP address.',
  city: 'City',
  cityDescription: 'The city of the visitor, determined by their IP address.',
  region: 'Region',
  regionDescription: 'The geographic region of the visitor, such as state or province.',
  continent: 'Continent',
  continentDescription: 'The continent where the visitor is located.',
  subContinent: 'Sub-continent',
  subContinentDescription: 'The sub-continent or regional grouping of the visitor\'s location.',
  metro: 'Metro area',
  metroDescription: 'The metropolitan area or designated market area (DMA) of the visitor.',

  // Device dimensions
  deviceType: 'Device type',
  deviceTypeDescription: 'The type of device used, such as mobile, desktop, or tablet.',
  deviceBrand: 'Device brand',
  deviceBrandDescription: 'The manufacturer or brand of the device used.',
  deviceModel: 'Device model',
  deviceModelDescription: 'The specific model of the device used.',
  deviceOs: 'Operating system',
  deviceOsDescription: 'The operating system running on the device.',
  deviceOsVersion: 'Operating system version',
  deviceOsVersionDescription: 'The specific version of the operating system.',
  deviceResolution: 'Screen resolution',
  deviceResolutionDescription: 'The screen resolution of the device in pixels (width × height).',

  // Browser dimensions
  browserName: 'Browser',
  browserNameDescription: 'The web browser used to access the site.',
  browserVersion: 'Browser version',
  browserVersionDescription: 'The specific version of the web browser.',
  browserEngine: 'Browser engine',
  browserEngineDescription: 'The underlying rendering engine used by the browser.',
  browserLocale: 'Browser locale',
  browserLocaleDescription: 'The locale (language and location) of the browser.',
  browserLanguage: 'Browser language',
  browserLanguageDescription: 'The language setting of the browser.',
  browserCookies: 'Cookies supported',
  browserCookiesDescription: 'Whether the browser supports cookies.',

  // Traffic source dimensions
  referrerType: 'Referrer type',
  referrerTypeDescription: 'The type of traffic source that referred the visit.',
  referrerName: 'Referrer name',
  referrerNameDescription: 'The name of the traffic source that referred the visit.',
  referrerUrl: 'Referrer URL',
  referrerUrlDescription: 'The URL of the traffic source that referred the visit.',
  referrerSearchEngine: 'Referring search engine',
  referrerSearchEngineDescription: 'The search engine that referred the visit.',
  referrerSearchKeyword: 'Referring search keyword',
  referrerSearchKeywordDescription: 'The search keyword that led to the visit, if applicable.',
  referrerWebsite: 'Referring website',
  referrerWebsiteDescription: 'The full URL of the referring website.',
  referrerCampaign: 'Referring campaign',
  referrerCampaignDescription: 'The campaign that referred the visit.',
  referrerSocialNetwork: 'Referring social network',
  referrerSocialNetworkDescription: 'The social network that referred the visit.',
  referrerAI: 'Referring AI assistant',
  referrerAIDescription: 'The AI assistant that referred the visit.',

  // Campaign dimensions
  campaignContent: 'Campaign content',
  campaignContentDescription: 'The content variation of the campaign.',
  campaignGroup: 'Campaign group',
  campaignGroupDescription: 'The group or category of the campaign.',
  campaignKeyword: 'Campaign keyword',
  campaignKeywordDescription: 'The keyword associated with the campaign.',
  campaignMedium: 'Campaign medium',
  campaignMediumDescription: 'The marketing medium used for the campaign (e.g., email, banner).',
  campaignName: 'Campaign name',
  campaignNameDescription: 'The name or ID of the marketing campaign.',
  campaignSource: 'Campaign source',
  campaignSourceDescription: 'The source of the campaign (e.g., google, newsletter).',
  campaignId: 'Campaign ID',
  campaignIdDescription: 'The ID of the marketing campaign.',
  campaignPlacement: 'Campaign placement',
  campaignPlacementDescription: 'The placement of the campaign (e.g., banner, email).',

  // Visitor dimensions
  visit: 'Visit',
  visitFirstActionTime: 'Visit first action time',
  visitFirstActionTimeDescription: 'The time when the visit started.',
  visitLastActionTime: 'Visit last action time',
  visitLastActionTimeDescription: 'The time when the visit ended.',
  visitorLocalHour: 'Visitor local hour',
  visitorLocalHourDescription: 'The hour of the day in the visitor\'s local time zone.',
  visitWebsiteHour: 'Visit website hour',
  visitWebsiteHourDescription: 'The hour of the day in the website\'s time zone.',
  visitCount: 'Visit count',
  visitCountDescription: 'The number of visits made by the visitor.',
  visitCountGrouped: 'Visit count grouped',
  visitCountGroupedDescription: 'The number of visits made by the visitor grouped into ranges.',
  visitDaysSinceFirstVisit: 'Days since first visit',
  visitDaysSinceFirstVisitDescription: 'The number of days since the first visit of the visitor.',
  visitDaysSinceLastVisit: 'Days since last visit',
  visitDaysSinceLastVisitDescription: 'The number of days since the last visit of the visitor.',
  visitDaysSinceLastVisitGrouped: 'Days since last visit grouped',
  visitDaysSinceLastVisitGroupedDescription: 'The number of days since the last visit of the visitor grouped into ranges.',
  visitEntryUrl: 'Entry URL',
  visitEntryUrlDescription: 'The URL of the page that the visitor first visited.',
  visitEntryTitle: 'Entry title',
  visitEntryTitleDescription: 'The title of the page that the visitor first visited.',
  visitExitUrl: 'Exit URL',
  visitExitUrlDescription: 'The URL of the page that the visitor last visited.',
  visitExitTitle: 'Exit title',
  visitExitTitleDescription: 'The title of the page that the visitor last visited.',

  // Actions
  actionUrl: 'Action URL',
  actionUrlDescription: 'The URL of the action.',
  actionName: 'Action name',
  actionNameDescription: 'The name of the action.',

  // Events
  eventCategory: 'Event category',
  eventCategoryDescription: 'The category of the event.',
  eventName: 'Event name',
  eventNameDescription: 'The name of the event.',
  eventAction: 'Event action',
  eventActionDescription: 'The action of the event.',
  eventUrl: 'Event URL',
  eventUrlDescription: 'The URL the event happened on.',
  eventUrlPath: 'Event URL path',
  eventUrlPathDescription: 'The path portion of the URL that the event happened on.',

  // Page dimensions
  pageTitle: 'Page title',
  pageTitleDescription: 'The title of the web page.',
  pageUrl: 'Page URL',
  pageUrlDescription: 'The full URL of the web page.',
  pagePath: 'Page path',
  pagePathDescription: 'The path portion of the page URL.',
  pagePathSegment: 'Path segment',
  pagePathSegmentDescription: 'Path from the page URL divided into segments for each slash.',
  pageQuery: 'Query parameters',
  pageQueryDescription: 'The query string parameters from the page URL.',
  pageFragment: 'URL fragment',
  pageFragmentDescription: 'The fragment identifier (hash) from the page URL.',
  pageReferrer: 'Page referrer',
  pageReferrerDescription: 'The referrer URL of the page.',

  siteSearchKeyword: 'Site search keyword',
  siteSearchKeywordDescription: 'The keyword used in the site search.',
  siteSearchCategory: 'Site search category',
  siteSearchCategoryDescription: 'The category of the site search.',

  // Other types of actions
  outlink: 'Outlink',
  outlinkDescription: 'The URL of the external link.',
  outlinkDomain: 'Outlink domain',
  outlinkDomainDescription: 'The domain of the external link.',
  outlinkPath: 'Outlink path',
  outlinkPathDescription: 'The path of the external link.',
  download: 'Download',
  downloadDescription: 'The URL of the download.',
  downloadDomain: 'Download domain',
  downloadDomainDescription: 'The domain of the download.',
  downloadPath: 'Download path',
  downloadPathDescription: 'The path of the download.',
  searchCategory: 'Search category',
  searchCategoryDescription: 'The category of the search done on the website.',

  // Content dimensions
  contentName: 'Content name',
  contentNameDescription: 'The name or title of the content.',
  contentPiece: 'Content piece',
  contentPieceDescription: 'A specific piece or section of content.',
  contentTarget: 'Content target',
  contentTargetDescription: 'The target or goal of the content.',
  contentType: 'Content type',
  contentTypeDescription: 'The type or category of content.',

  // Survey dimensions
  questionType: 'Question type',
  questionTypeDescription: 'The type of the survey question.',
  surveyPageUrl: 'Survey page URL',
  surveyPageUrlDescription: 'The URL of the survey page.',
  surveyPageTitle: 'Survey page title',
  surveyPageTitleDescription: 'The title of the survey page.',

  goal: 'Goal',
  goalDescription: 'The goal that was converted during the visit.',
});
